import * as React from 'react';
import { isAnonymousAccountProjectIdFixEnabled } from 'feature-flags';
import type { CreateUIAnalyticsEvent, WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import { OPERATIONAL_EVENT_TYPE, TRACK_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { ViewPage } from '@atlassian/embedded-confluence';
import type { ExperienceEvent } from '@atlassian/experience-tracker';
import { ExperienceTracker, ExperienceTrackerContext } from '@atlassian/experience-tracker';
import { EMBEDDED_CONFLUENCE_SHIM_URL, PORTAL_PARENT_PRODUCT } from '@atlassian/help-center-common-component/constants';
import {
    getSlugForAdvancedHCFromArticleView,
    isAdvancedHelpCenter,
} from '@atlassian/help-center-common-util/advanced-help-center/advanced-help-center';
import { href } from '@atlassian/help-center-common-util/location';

const { useEffect, useState } = React;
export interface ConfluenceEmbeddedViewProps {
    articleId: string;
    spaceKey: string | undefined;
    projectId: number | undefined;
    onIFrameLoaded?: () => void;
    isAnonymousAccount: boolean;
    kbLinkDomainURLs: string[];
}

export const experienceTrackerCallback = (
    payload: ExperienceEvent,
    articleId: string,
    onIFrameLoaded?: () => void,
    createAnalyticsEvent?: CreateUIAnalyticsEvent
) => {
    const { action, name, id } = payload;
    switch (action) {
        case 'taskSuccess':
            if (name === 'view-content' && id === articleId) {
                createAnalyticsEvent &&
                    createAnalyticsEvent({
                        action: 'portalEpView success',
                        analyticsType: TRACK_EVENT_TYPE,
                        actionSubject: 'KBArticle',
                    }).fire();
                onIFrameLoaded && onIFrameLoaded();
            }
            break;
        case 'taskFail':
            if (name === 'view-content' && id === articleId) {
                createAnalyticsEvent &&
                    createAnalyticsEvent({
                        action: 'portalEpView failure',
                        analyticsType: OPERATIONAL_EVENT_TYPE,
                    }).fire();
            }
            break;
        default:
            break;
    }
};

export const ConfluenceArticleViewPage = ({
    articleId,
    spaceKey,
    projectId,
    onIFrameLoaded,
    createAnalyticsEvent,
    isAnonymousAccount,
    kbLinkDomainURLs,
}: ConfluenceEmbeddedViewProps & WithAnalyticsEventsProps) => {
    const [experienceTracker] = useState(new ExperienceTracker());

    useEffect(() => {
        return experienceTracker.subscribe((payload: ExperienceEvent) =>
            experienceTrackerCallback(payload, articleId, onIFrameLoaded, createAnalyticsEvent)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experienceTracker, articleId]);

    const getParentProductContentContainerId = () => {
        if (isAnonymousAccountProjectIdFixEnabled() && isAnonymousAccount) {
            return undefined;
        }

        return projectId !== undefined ? String(projectId) : undefined;
    };

    const getShimURLForAdvancedHCLinkedArticle = () => {
        const slug = getSlugForAdvancedHCFromArticleView(href());
        return slug ? `/plugins/servlet/helpcenter/${slug}/confluence/shim/` : undefined;
    };

    const navigationPolicy = {
        shimUrl: isAdvancedHelpCenter() ? getShimURLForAdvancedHCLinkedArticle() : EMBEDDED_CONFLUENCE_SHIM_URL,
        domainNamesToReplace: kbLinkDomainURLs.map((url: string) => url.replace(/^https?:\/\//, '')),
    };

    return (
        <ExperienceTrackerContext.Provider value={experienceTracker}>
            <ViewPage
                allowedFeatures={['disable-login-flow', 'disable-share']}
                hasFooterLogo={false}
                contentId={articleId}
                parentProduct={PORTAL_PARENT_PRODUCT}
                parentProductContentContainerId={getParentProductContentContainerId()}
                spaceKey={spaceKey}
                navigationPolicy={navigationPolicy}
            />
        </ExperienceTrackerContext.Provider>
    );
};

export const ConfluenceEmbeddedView = withAnalyticsEvents()(ConfluenceArticleViewPage);
